import { useMemo } from "react";
import {
  AssessmentQuestionType,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyReportRowFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";

import { assessmentQuestionIcon } from "@apps/programs/components/compliance-program-form/assessment-group-sidebar/assessment-group-sidebar-question-constants";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import { TableSticky } from "@components/table/table-constants";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import {
  getNpsColorValue,
  getRangeColorValue,
  getResponderKey,
  isHighContrastBackground,
} from "./helpers";
import NpsQuestionRubric from "./nps-question-rubric";
import RangeQuestionRubric from "./range-question-rubric";

export const resultColWidth = "w-48 max-w-48 min-w-48";

const RollupHeading = ({
  responder,
}: {
  responder: EngagementSurveyReportResponderFragment;
}): string | JSX.Element => {
  const label = useLabel();
  switch (responder.rollupType) {
    case EngagementSurveyRollupType.Organization:
      return label("organization", { capitalize: true });

    case EngagementSurveyRollupType.Team:
      return assertNonNull(responder.team?.title);

    case EngagementSurveyRollupType.CareerTrack:
      return assertNonNull(responder.careerTrack?.title);

    case EngagementSurveyRollupType.Location:
      return assertNonNull(responder.location);

    case EngagementSurveyRollupType.IsManager:
      return `${responder.isManager ? "Managers" : "ICs"}`;

    case EngagementSurveyRollupType.Manager:
      return (
        <div className="flex items-center gap-2">
          <Avatar size="5" user={responder.manager} />
          {responder.manager?.name}
        </div>
      );
    case EngagementSurveyRollupType.Collapsed:
      return "Other";
  }
};

const EngagementSurveyReportTableRow = ({
  question,
  visibleResponders,
}: {
  question: EngagementSurveyReportRowFragment;
  visibleResponders: EngagementSurveyReportResponderFragment[];
}) => {
  const QuestionIcon =
    question.question?.__typename === "RangeAssessmentQuestionNode"
      ? assessmentQuestionIcon[AssessmentQuestionType.Range]
      : assessmentQuestionIcon[AssessmentQuestionType.Nps];

  return (
    <TableBodyRow key={question.question.id}>
      <TableBodyCell className="w-96 max-w-96 min-w-96">
        <div className="flex flex-col gap-1">
          <div>{question.title || "Unnamed Section"}</div>
          {question.question && (
            <div className="text-xs text-gray-400 flex items-center gap-2">
              <QuestionIcon className="h-4 w-4" />
              {question.question?.__typename === "RangeAssessmentQuestionNode"
                ? `Range (${question.question?.startValue}-${question.question?.endValue})`
                : "eNPS"}
            </div>
          )}
        </div>
      </TableBodyCell>
      {visibleResponders.map((responder, columnIndex) => {
        const rollup = question.rollups.edges
          .map((edge) => assertNonNull(edge?.node))
          .find(
            (rollup) =>
              rollup.rollupType === responder.rollupType &&
              getResponderKey(rollup) === getResponderKey(responder),
          );

        const value = rollup?.value ? rollup.value : 0;
        const backgroundColor =
          responder.responderCount === 0 || responder.isHidden
            ? "#ffffff"
            : question.question?.__typename === "RangeAssessmentQuestionNode"
              ? getRangeColorValue((value / question.question?.endValue) * 100)
              : getNpsColorValue(value);
        return (
          <TableBodyCell
            key={`${responder.rollupType}-${getResponderKey(responder)}`}
            className={classNames(
              resultColWidth,
              columnIndex < visibleResponders.length - 1 && "border-r",
              !isHighContrastBackground(backgroundColor) && "text-white",
              "p-4 text-center align-middle",
            )}
            style={{ backgroundColor }}
          >
            {responder.isHidden ? (
              <div className="flex flex-col items-center gap-2.5 text-gray-400">
                <Tooltip text="To keep identities anonymous, we cannot display data when there is less than 3 people within a group.">
                  <span>
                    <TopicflowIcon type="info" className="h-4 w-4" />
                  </span>
                </Tooltip>
                <div className="text-sm">
                  Not enough responses to show results
                </div>
              </div>
            ) : responder.responderCount === 0 ? (
              <div className="text-sm text-gray-400">-</div>
            ) : question.question?.__typename ===
              "RangeAssessmentQuestionNode" ? (
              <div className="text-sm font-semibold flex items-center justify-center gap-1">
                {value}
                <div className="opacity-50">
                  / {question.question?.endValue}
                </div>
              </div>
            ) : (
              <div className="text-sm font-semibold">{value}</div>
            )}
          </TableBodyCell>
        );
      })}
    </TableBodyRow>
  );
};

const EngagementSurveyReportTableQuestionTypeHeading = ({
  questionType,
}: {
  questionType: AssessmentQuestionType;
}) => {
  return (
    <TableBodyRow>
      <TableBodyCell>
        <div className="flex items-center gap-3">
          <div className="font-bold">
            {questionType === AssessmentQuestionType.Nps
              ? "eNPS Results"
              : "Range Question Results"}
          </div>
          {questionType === AssessmentQuestionType.Nps ? (
            <NpsQuestionRubric />
          ) : (
            <RangeQuestionRubric />
          )}
        </div>
      </TableBodyCell>
    </TableBodyRow>
  );
};

const EngagementSurveyReportTable = ({
  responders,
  npsQuestionRows,
  rangeQuestionRows,
}: {
  responders: EngagementSurveyReportResponderFragment[];
  npsQuestionRows: EngagementSurveyReportRowFragment[];
  rangeQuestionRows: EngagementSurveyReportRowFragment[];
}) => {
  const visibleResponders = useMemo(
    () => responders.filter(({ isCollapsed }) => !isCollapsed),
    [responders],
  );

  return (
    <TableContainer scroll className="max-h-full">
      <Table className="w-full" sticky={TableSticky.topAndLeft}>
        <TableHeadRow>
          <TableHeadCell isFirst>Question</TableHeadCell>
          {visibleResponders.map((responder) => {
            return (
              <TableHeadCell
                key={`${responder.rollupType}-${getResponderKey(responder)}`}
              >
                <div className="flex flex-col items-center gap-1">
                  <RollupHeading responder={responder} />
                  <div className="text-xs flex items-center gap-1">
                    <TopicflowIcon type="user" className="h-3 w-3" />
                    <div className="flex items-center gap-1">
                      <div>{responder.responderCount}</div>
                      <div className="text-gray-400">/</div>
                      <div className="text-gray-400">
                        {responder.totalResponders}
                      </div>
                    </div>
                  </div>
                </div>
              </TableHeadCell>
            );
          })}
        </TableHeadRow>
        <TableBody>
          {npsQuestionRows.length > 0 && (
            <EngagementSurveyReportTableQuestionTypeHeading
              questionType={AssessmentQuestionType.Nps}
            />
          )}
          {npsQuestionRows.map((question) => {
            return (
              <EngagementSurveyReportTableRow
                key={question.question.id}
                question={question}
                visibleResponders={visibleResponders}
              />
            );
          })}

          {rangeQuestionRows.length > 0 && (
            <EngagementSurveyReportTableQuestionTypeHeading
              questionType={AssessmentQuestionType.Range}
            />
          )}
          {rangeQuestionRows.map((question) => {
            return (
              <EngagementSurveyReportTableRow
                key={question.question.id}
                question={question}
                visibleResponders={visibleResponders}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EngagementSurveyReportTable;
